import { apiFetch, apiFetchBlob } from "./fetch";
import type { Issue } from "./issues";

// --- Enums --------------------------------------------------------------

export type ScanStatus =
  | "queued"
  | "running"
  | "complete"
  | "failed"
  | "interrupted"
  | "aborted";

export type ScanTier = "free" | "pro" | "team" | "enterprise";

export type ScanIntensity = "passive" | "probe" | "explore" | "adversarial";

// Reachability posture stamped per scan by the platform. `cli-proxy` is only
// reachable from `gated scan`; the dashboard renders a "CLI" badge for it.
export type ScanMode = "direct" | "cli-proxy";

export type ScanCheckStatus =
  | "pending"
  | "running"
  | "complete"
  | "failed"
  | "irrelevant"
  | "interrupted"
  | "aborted";

// --- Types --------------------------------------------------------------

export interface ScanCounts {
  total: number;
  findings: number;
  observations: number;
}

export interface Scan {
  id: string;
  displayId: string;
  targetId: string;
  targetUrl: string;
  targetDisplayName: string;
  tier: ScanTier;
  intensity: ScanIntensity;
  scanMode: ScanMode;
  status: ScanStatus;
  statusReason?: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  counts?: ScanCounts;
}

export interface CheckCatalog {
  title: string;
  description: string;
  criteria: string;
  remediation: string;
  references: string[];
  category: string;
  family: string;
  intensity: string;
  hidden: boolean;
  // Platform serialises catalog dependencies as the upstream `check_id`
  // strings — the typed Dependency{Kind, KeyIn} shape from signals
  // gating lives in the Go check library and isn't exposed in this
  // route's response.
  dependsOn: string[];
}

export interface ScanCheck {
  id: string;
  checkId: string;
  checkVersion: string;
  status: ScanCheckStatus;
  error: string | null;
  waveIndex: number | null;
  irrelevantByCheckId: string | null;
  startedAt: string | null;
  completedAt: string | null;
  catalog: CheckCatalog | null;
}

export interface FindingContributor {
  type: string;
  value: string;
}

export interface Finding {
  id: string;
  displayId: string;
  // Adversarial findings have no owning scan_check; the platform may emit
  // either null or "" depending on its serializer. Normalize both to null.
  scanCheckId: string | null;
  issueId: string | null;
  checkId: string;
  category: string;
  severity: string;
  confidence: string;
  evidence: unknown;
  remediation: string;
  title: string;
  summary: string;
  titleTemplate: string | null;
  contributors: FindingContributor[] | null;
  // Deterministic harness calls this finding's check made (the exact trace),
  // streamed alongside evidence. Empty/absent for adversarial findings and
  // until the heavy report section folds in. See ScanTraceSample.
  trace?: ScanTraceSample[];
  createdAt: string;
}

export interface SurfaceToolVariant {
  identityLabels: string[];
  description: string | null;
  inputSchema: unknown;
}

export interface SurfaceTool {
  id: string;
  name: string;
  description: string | null;
  inputSchema: unknown;
  outputSchema: unknown;
  identityLabels: string[];
  variants: SurfaceToolVariant[] | null;
  tokenCount: number | null;
  createdAt: string;
}

export interface SurfacePromptArgument {
  name: string;
  description: string | null;
  required: boolean;
}

export interface SurfacePrompt {
  id: string;
  name: string;
  description: string | null;
  arguments: SurfacePromptArgument[];
  identityLabels: string[];
  tokenCount: number | null;
  createdAt: string;
}

export interface SurfaceResource {
  id: string;
  uri: string;
  name: string | null;
  description: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  isTemplate: boolean;
  identityLabels: string[];
  tokenCount: number | null;
  createdAt: string;
}

export interface SurfaceTokenTotal {
  tools: number;
  prompts: number;
  resources: number;
  initialize: number;
  total: number;
  protocolVersion: string | null;
}

export interface ScanIdentityResult {
  label: string;
  status: "ok" | "decrypt_failed";
  error: string;
}

export interface ScanDetail {
  id: string;
  displayId: string;
  targetId: string;
  targetUrl: string;
  targetDisplayName: string;
  tier: ScanTier;
  intensity: ScanIntensity;
  scanMode: ScanMode;
  status: ScanStatus;
  statusReason?: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  identityResults: ScanIdentityResult[] | null;
  identityIds: string[] | null;
  // MCP `capabilities` object the target server advertised during the
  // `initialize` handshake. Rendered as color-coded pills in the scan
  // detail header. Null until the scan.surface event lands.
  capabilities: Record<string, unknown> | null;
  checkIds: string[];
  // True once abort has been requested but the scan has not yet reached
  // the terminal `aborted` status — drives the "Aborting…" header state.
  abortRequested: boolean;
}

export interface ScanProgressEvent {
  id: string;
  occurredAt: string;
  // scan.progress carries only scan-level phases (orchestrator track from
  // the worker, platform track from the platform's own dispatch steps).
  // Per-check lifecycle is now check.started / check.completed events.
  track: "platform" | "orchestrator";
  phase: string;
  message: string;
  attrs: Record<string, unknown>;
}

// --- Trace (Tool Calls tab) ---------------------------------------------
// Every MCP call the scan made (deterministic waves + adversarial phase),
// aggregated by the platform from harness_log and grouped by MCP method.
export type ScanTraceGroupKind = "discovery" | "tool" | "resource" | "prompt";

export interface ScanTraceSample {
  t: string; // ISO timestamp
  issuer: string; // issuing check id or hypothesis id
  actor: string | null; // identity label the call ran as
  status: number | null; // synthetic HTTP-ish status
  durationMs: number | null;
  // Raw (unscrubbed) request args + response body — surfaced only here.
  args: string | null;
  response: string | null;
}

export interface ScanTraceEndpoint {
  method: string; // tools/call, resources/read, …
  target: string; // tool name / resource uri / "—" for discovery
  calls: number;
  errors: number;
  p50Ms: number | null;
  lastAt: string | null;
  issuers: string[];
  samples: ScanTraceSample[];
}

export interface ScanTraceGroup {
  kind: ScanTraceGroupKind;
  label: string;
  sub: string;
  endpoints: ScanTraceEndpoint[];
}

export interface ScanTrace {
  totalCalls: number;
  endpointCount: number;
  // LLM inspections consumed this scan (billing unit); 0 without an
  // adversarial phase. Shown in the Trace stat strip.
  inspectionsUsed: number;
  groups: ScanTraceGroup[];
}

export interface ScanReport {
  scan: ScanDetail;
  checks: ScanCheck[];
  findings: Finding[];
  // Issues touched by this scan — findings collapsed by issueId, carrying the
  // I-… display id and open/resolved/suppressed status. `findings` is kept
  // for per-finding evidence drill-down inside an issue.
  issues: Issue[];
  surfaceTools: SurfaceTool[];
  surfacePrompts: SurfacePrompt[];
  surfaceResources: SurfaceResource[];
  progressEvents: ScanProgressEvent[];
  surfaceTokenTotal: SurfaceTokenTotal;
  // Tool-call trace (Trace tab). Counts arrive on the skeleton; `groups`
  // streams in via the `trace` report section.
  trace: ScanTrace;
}

export interface ScanEvent {
  id: string;
  type: string;
  occurredAt: string;
  payload: unknown;
}

export interface FindingsBySeverity {
  crit: number;
  high: number;
  med: number;
  low: number;
  info: number;
}

export interface RecentScan {
  id: string;
  displayId: string;
  status: ScanStatus;
  scanMode: ScanMode;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  findingsBySeverity: FindingsBySeverity;
}

export interface CreateScanResponse {
  scanId: string;
  targetId: string;
  seededChecks: number;
}

export interface AbortScanResponse {
  ok: boolean;
}

// --- Endpoints ----------------------------------------------------------

export function listScans(signal?: AbortSignal): Promise<{ scans: Scan[] }> {
  return apiFetch<{ scans: Scan[] }>("/api/scans", { signal });
}

export function listScansForTarget(
  targetId: string,
  signal?: AbortSignal,
): Promise<{ scans: RecentScan[] }> {
  return apiFetch<{ scans: RecentScan[] }>(
    `/api/targets/${encodeURIComponent(targetId)}/scans`,
    { signal },
  );
}

/**
 * Fetches the server-rendered MCP Audit Report PDF as a `Blob`. Goes through
 * `apiFetchBlob` so the request carries the Clerk bearer token — the endpoint
 * is auth-gated like every other `/api/scans` route.
 */
export function getScanReportPdf(
  id: string,
  signal?: AbortSignal,
): Promise<Blob> {
  return apiFetchBlob(`/api/scans/${encodeURIComponent(id)}/report.pdf`, {
    signal,
  });
}

/**
 * Fetches a plain-text log of every event recorded for the scan
 * (worker→platform→dashboard chain). One line per event. Auth-gated; goes
 * through `apiFetchBlob` so the Clerk bearer token is attached.
 */
export function getScanEventsLog(
  id: string,
  signal?: AbortSignal,
): Promise<Blob> {
  return apiFetchBlob(`/api/scans/${encodeURIComponent(id)}/events.log`, {
    signal,
  });
}

export interface CreateScanBody {
  intensity?: ScanIntensity;
  identityIds?: string[];
  /**
   * Caller has confirmed they own the target and authorize an adversarial
   * scan. Required for adversarial intensity; worker refuses without it.
   */
  adversarialOptIn?: boolean;
}

export function createScan(
  targetId: string,
  body: CreateScanBody = {},
): Promise<CreateScanResponse> {
  return apiFetch<CreateScanResponse>(`/api/targets/${targetId}/scans`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function abortScan(scanId: string): Promise<AbortScanResponse> {
  return apiFetch<AbortScanResponse>(
    `/api/scans/${encodeURIComponent(scanId)}/abort`,
    { method: "POST" },
  );
}

export async function deleteScan(scanId: string): Promise<void> {
  await apiFetch<void>(`/api/scans/${encodeURIComponent(scanId)}`, {
    method: "DELETE",
  });
}

// Platform may emit `scanCheckId: ""` for adversarial findings on some code
// paths. Normalize to null so consumers can rely on `f.scanCheckId == null`.
export function normalizeScanReport(report: ScanReport): ScanReport {
  for (const f of report.findings) {
    if (f.scanCheckId === "") f.scanCheckId = null;
  }
  return report;
}
